import * as THREE from 'three';
import { buildInsectScreen } from '../scene/insectScreen';
import { box } from '../scene/products';
import { applyMetricUVs } from '../scene/materialGeometry';
import { panelArray } from './panelArray';
import { INCH } from './rules';
import { stackLayout, panelPositionsAt } from './stackLayout';
import { makeCustomProfile } from './multiTrackProfile';
import { runMeasurements } from './runMeasurements';
import { dividerArray } from './dividerArray';
import { makeSD150Replacement } from './sd150Model';
import { makeFixedWindowReplacement } from './fixedWindowModel';
import { makeBifoldReplacement, makeSwingReplacement, makePivotReplacement } from './bifoldModel';
import { makeSlidingHandle, positionSlidingHandle } from '../scene/slidingHandle';
import { hasSlidingMesh } from './slidingOptions';

function makeRun(run, design, product, materials, frame, side) {
  const group = new THREE.Group(), h = design.height_in * INCH, corner = design.layout === 'corner';
  const layout = stackLayout(run, product, corner, side);
  const { width: w, count, tracks, depth, trackCenter, panelWidth: pw, pocket } = layout;
  const family = product.track_count === 3 ? 'three' : 'two';
  const add = (part, x, y, z = 0) => { part.position.set(x, y, z); group.add(part); return part; };
  const profile = (kind, length) => makeCustomProfile(tracks, kind, length, frame, materials);
  // The head/sill join horizontally; there is no stationary corner upright.
  const railEnd = corner ? (side === 'a' ? -.0375 : stackLayout(design.run_a, product, true, 'a').depth - .0375) : 0;
  add(profile('sill', w + pocket + railEnd), (-pocket + railEnd) / 2, 0, trackCenter);
  add(profile('head', w + pocket + railEnd), (-pocket + railEnd) / 2, h - .05, trackCenter);
  const left = profile('jamb', h); left.rotation.z = -Math.PI / 2; add(left, -w / 2 - pocket, h / 2, trackCenter);
  if (!corner) { const right = profile('jamb', h); right.rotation.z = Math.PI / 2; add(right, w / 2, h / 2, trackCenter); }
  if (pocket) {
    for (const z of [-1, 1]) box(group, pocket, h + .08, .06, -w / 2 - pocket / 2, (h + .08) / 2, trackCenter + z * (depth / 2 + .035), materials.wall);
    box(group, pocket, .08, depth + .12, -w / 2 - pocket / 2, h + .04, trackCenter, materials.wall);
  }
  const update = panelArray(group, family, count, pw, h, frame, materials);
  const updateDividers = dividerArray(group, count, pw, h, design.dividers, frame);
  const handle = makeSlidingHandle({ system: 'MT150', panelWidth: pw, panelHeight: h, pocket: !!pocket, face: corner && side === 'b' ? -1 : 1, panelIndex: count - 1 }, materials); group.add(handle);
  const animate = fraction => {
    const next = panelPositionsAt(layout, fraction);
    updateDividers(next);
    update(next); positionSlidingHandle(handle, next[count - 1]);
    group.userData.panelPositions = next.map(p => ({ x: +p.x.toFixed(4), z: +p.z.toFixed(4) }));
  };
  const screen = hasSlidingMesh(design) ? buildInsectScreen(group, w, h, depth, frame, materials.steel, corner ? {
    openCorner: true, railZ: side === 'a' ? .11 : -.11,
    right: w / 2 - .11 + (side === 'a' ? -.009 : .013),
  } : {}) : null;
  const measures = runMeasurements(w, h, run.width_in, corner, side); group.add(measures);
  animate(design.openings[side] / 100); screen?.animate(design.screens[side] / 100);
  return { group, measures, animate, screen, width: w, height: h, panelWidth: pw, count, tracks, depth, side, layout };
}

export function makeCustomDoor(design, product, materials, frame) {
  if (product.is_fixed) return makeFixedWindowReplacement(design, product, materials, frame);
  if (product.kind === 'bifold') return makeBifoldReplacement(design, product, materials, frame);
  if (product.kind === 'swing') return makeSwingReplacement(design, product, materials, frame);
  if (product.kind === 'pivot') return makePivotReplacement(design, product, materials, frame);
  if (design.model_id === 'sd-150') return makeSD150Replacement(design, materials, frame);
  const group = new THREE.Group(), a = makeRun(design.run_a, design, product, materials, frame, 'a');
  group.add(a.group); const runs = [a];
  if (design.layout === 'corner') {
    const b = makeRun(design.run_b, design, product, materials, frame, 'b'); runs.push(b); group.add(b.group);
    a.group.position.x = -a.width / 2;
    b.group.rotation.y = Math.PI / 2; b.group.position.z = b.width / 2;
  }
  applyMetricUVs(group);
  return { group, runs };
}

export function disposeModel(group) {
  const geometries = new Set(), ownMaterials = new Set();
  group.traverse(o => {
    if (o.geometry) geometries.add(o.geometry);
    for (const mat of o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []) if (!mat.userData.managed) ownMaterials.add(mat);
  });
  geometries.forEach(g => g.dispose()); ownMaterials.forEach(m => { m.map?.dispose(); m.dispose(); });
}